import { useLogicModel } from '../../../contexts/LogicModelContext';

export function ReviewStep() {
  const { logicModel, setCurrentStep } = useLogicModel();

  const counts = {
    inputs: logicModel?.inputs?.length || 0,
    activities: logicModel?.activities?.length || 0,
    outputs: logicModel?.outputs?.length || 0,
    outcomes: logicModel?.outcomes?.length || 0,
    impact_goals: logicModel?.impact_goals?.length || 0,
  };

  const totalIndicators = logicModel?.outcomes?.reduce(
    (sum, o) => sum + (o.indicators?.length || 0), 0
  ) || 0;

  const isComplete = counts.inputs > 0 && counts.activities > 0 && 
                     counts.outputs > 0 && counts.outcomes > 0 && counts.impact_goals > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          ✅ Review Your Logic Model
        </h2>
        <p className="text-gray-600">
          Here's your complete theory of change showing the if-then chain from Inputs to Impact.
        </p>
      </div>

      {/* Completeness Check */}
      <div className={`rounded-lg p-4 ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center">
          {isComplete ? (
            <>
              <span className="text-2xl mr-3">✅</span>
              <div>
                <h3 className="font-medium text-green-900">Logic Model Complete!</h3>
                <p className="text-sm text-green-700">All sections have at least one element defined.</p>
              </div>
            </>
          ) : (
            <>
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <h3 className="font-medium text-amber-900">Logic Model Incomplete</h3>
                <p className="text-sm text-amber-700">
                  Missing: {[
                    counts.inputs === 0 && 'Inputs',
                    counts.activities === 0 && 'Activities',
                    counts.outputs === 0 && 'Outputs',
                    counts.outcomes === 0 && 'Outcomes',
                    counts.impact_goals === 0 && 'Impact Goals',
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Visual Logic Chain */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4 text-center">Logic Model Flow</h3>
        
        <div className="flex flex-col md:flex-row items-stretch justify-between gap-2 md:gap-1">
          {/* Inputs */}
          <div 
            className="flex-1 bg-white rounded-lg p-4 shadow-sm border-2 border-orange-200 cursor-pointer hover:border-orange-400 hover:shadow-md transition-all"
            onClick={() => setCurrentStep('inputs')}
          >
            <div className="text-center">
              <span className="text-3xl">📦</span>
              <h4 className="font-medium text-gray-900 mt-2">Inputs</h4>
              <p className="text-3xl font-bold text-orange-600 my-1">{counts.inputs}</p>
              <p className="text-xs text-gray-500">resources</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center text-gray-400 text-2xl">
            <span className="hidden md:block">→</span>
            <span className="md:hidden">↓</span>
          </div>
          
          {/* Activities */}
          <div 
            className="flex-1 bg-white rounded-lg p-4 shadow-sm border-2 border-green-200 cursor-pointer hover:border-green-400 hover:shadow-md transition-all"
            onClick={() => setCurrentStep('activities')}
          >
            <div className="text-center">
              <span className="text-3xl">⚡</span>
              <h4 className="font-medium text-gray-900 mt-2">Activities</h4>
              <p className="text-3xl font-bold text-green-600 my-1">{counts.activities}</p>
              <p className="text-xs text-gray-500">interventions</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center text-gray-400 text-2xl">
            <span className="hidden md:block">→</span>
            <span className="md:hidden">↓</span>
          </div>
          
          {/* Outputs */}
          <div 
            className="flex-1 bg-white rounded-lg p-4 shadow-sm border-2 border-purple-200 cursor-pointer hover:border-purple-400 hover:shadow-md transition-all"
            onClick={() => setCurrentStep('outputs')}
          >
            <div className="text-center">
              <span className="text-3xl">📊</span>
              <h4 className="font-medium text-gray-900 mt-2">Outputs</h4>
              <p className="text-3xl font-bold text-purple-600 my-1">{counts.outputs}</p>
              <p className="text-xs text-gray-500">deliverables</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center text-gray-400 text-2xl">
            <span className="hidden md:block">→</span>
            <span className="md:hidden">↓</span>
          </div>
          
          {/* Outcomes */}
          <div 
            className="flex-1 bg-white rounded-lg p-4 shadow-sm border-2 border-amber-200 cursor-pointer hover:border-amber-400 hover:shadow-md transition-all"
            onClick={() => setCurrentStep('outcomes')}
          >
            <div className="text-center">
              <span className="text-3xl">📈</span>
              <h4 className="font-medium text-gray-900 mt-2">Outcomes</h4>
              <p className="text-3xl font-bold text-amber-600 my-1">{counts.outcomes}</p>
              <p className="text-xs text-gray-500">{totalIndicators} indicators</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center text-gray-400 text-2xl">
            <span className="hidden md:block">→</span>
            <span className="md:hidden">↓</span>
          </div>
          
          {/* Impact */}
          <div 
            className="flex-1 bg-white rounded-lg p-4 shadow-sm border-2 border-blue-200 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
            onClick={() => setCurrentStep('impact')}
          >
            <div className="text-center">
              <span className="text-3xl">🎯</span>
              <h4 className="font-medium text-gray-900 mt-2">Impact</h4>
              <p className="text-3xl font-bold text-blue-600 my-1">{counts.impact_goals}</p>
              <p className="text-xs text-gray-500">goals</p>
            </div>
          </div>
        </div>
        
        <p className="text-center text-sm text-gray-500 mt-4">
          Click any element to edit
        </p>
      </div>

      {/* Detailed Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Impact Goals */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <span className="mr-2">🎯</span> Impact Goals
          </h4>
          {logicModel?.impact_goals?.length ? (
            <ul className="space-y-2">
              {logicModel.impact_goals.map((goal) => (
                <li key={goal.id} className="text-sm text-gray-600 flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>{goal.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No impact goals defined</p>
          )}
        </div>

        {/* Outcomes */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <span className="mr-2">📈</span> Outcomes
          </h4>
          {logicModel?.outcomes?.length ? (
            <ul className="space-y-2">
              {logicModel.outcomes.map((outcome) => (
                <li key={outcome.id} className="text-sm text-gray-600">
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <div>
                      <span>{outcome.name}</span>
                      {outcome.indicators?.length > 0 && (
                        <span className="text-xs text-gray-400 ml-2">
                          ({outcome.indicators.length} indicator{outcome.indicators.length !== 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No outcomes defined</p>
          )}
        </div>

        {/* Outputs */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <span className="mr-2">📊</span> Outputs
          </h4>
          {logicModel?.outputs?.length ? (
            <ul className="space-y-2">
              {logicModel.outputs.map((output) => (
                <li key={output.id} className="text-sm text-gray-600 flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>{output.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No outputs defined</p>
          )}
        </div>

        {/* Activities */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <span className="mr-2">⚡</span> Activities
          </h4>
          {logicModel?.activities?.length ? (
            <ul className="space-y-2">
              {logicModel.activities.map((activity) => (
                <li key={activity.id} className="text-sm text-gray-600 flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  <span>{activity.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No activities defined</p>
          )}
        </div>
      </div>

      {/* If-Then Statement */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">📖 Reading Your Logic Model</h3>
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>IF</strong> we have these <span className="text-orange-600 font-medium">inputs</span>, <strong>THEN</strong> we can do these <span className="text-green-600 font-medium">activities</span> →
          <strong> IF</strong> we do these activities, <strong>THEN</strong> we produce these <span className="text-purple-600 font-medium">outputs</span> →
          <strong> IF</strong> we produce these outputs, <strong>THEN</strong> participants experience these <span className="text-amber-600 font-medium">outcomes</span> →
          <strong> IF</strong> these outcomes occur, <strong>THEN</strong> we contribute to long-term <span className="text-blue-600 font-medium">impact</span>.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 justify-center pt-4">
        <button
          className="px-6 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
          onClick={() => window.print()}
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
        <button
          disabled
          className="px-6 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-400 cursor-not-allowed flex items-center"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export PDF (Coming Soon)
        </button>
      </div>
    </div>
  );
}
